/**
 * API服务
 */
import axios from 'axios';

// API Base URL 配置
// 开发环境：.env 中配置 REACT_APP_API_URL=http://localhost:8000
// 生产环境：.env.production 中配置 REACT_APP_API_URL=（空字符串，使用相对路径）
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 调整为60秒
});

// 请求拦截器 - 自动带 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    // 检测是否是AI相关的API调用（会消耗Token）
    const url = response.config.url || '';
    const aiEndpoints = [
      '/api/content/generate',
      '/api/outline/generate',
      '/api/projects/analyze',
      '/api/check/',
      '/api/content/generate-table',
      '/api/content/generate-mermaid',
      '/api/document/extract',
      '/api/expand/',
      '/api/projects/extract-license'
    ];

    // 如果是AI调用，触发Token刷新事件
    if (aiEndpoints.some(endpoint => url.includes(endpoint))) {
      console.log('✨ AI调用完成，触发Token刷新事件:', url);
      window.dispatchEvent(new CustomEvent('token:used'));
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 错误且未重试过
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('[认证] 收到 401 响应，开始处理...', {
        url: originalRequest.url,
        hasRefreshToken: !!localStorage.getItem('refresh_token')
      });

      if (isRefreshing) {
        console.log('[认证] 正在刷新 token，请求加入队列');
        // 正在刷新 token，将请求加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        console.log('[认证] 没有 refresh_token，触发登录弹框');
        // 没有 refresh_token，触发自定义事件
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        console.log('[认证] 尝试刷新 token...');
        // 尝试刷新 token
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refresh_token: refreshToken
        });

        console.log('[认证] Token 刷新成功');
        const { access_token, refresh_token: newRefreshToken, user } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', newRefreshToken);
        localStorage.setItem('user', JSON.stringify(user));

        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);
        isRefreshing = false;
        console.log('[认证] 重试原请求...');
        return api(originalRequest);
      } catch (err) {
        console.log('[认证] Token 刷新失败，触发登录弹框', err);
        processQueue(err, null);
        // 刷新失败，触发自定义事件
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        isRefreshing = false;
        return Promise.reject(err);
      }
    }

    // 检查是否是 Token 相关错误
    // 402: 欠费但未超过最大欠费额度（弹出告警，用户可选择继续）
    // 400: 超过最大欠费额度（拒绝，必须充值）
    if (error.response?.status === 402) {
      const errorDetail = error.response?.data?.detail || '';
      console.log('Token 欠费警告，触发警告事件');
      window.dispatchEvent(new CustomEvent('token:warning', {
        detail: { message: errorDetail }
      }));
    } else if (error.response?.status === 400) {
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.includes('Token') && (errorDetail.includes('欠费') || errorDetail.includes('不足') || errorDetail.includes('额度'))) {
        console.log('Token 已超过最大欠费额度，触发欠费事件');
        window.dispatchEvent(new CustomEvent('token:overdraft', {
          detail: { message: errorDetail }
        }));
      }
    }

    console.error('API请求错误:', error);
    return Promise.reject(error);
  }
);

export interface ConfigData {
  api_key: string;
  base_url?: string;
  model_name: string;
}

export interface FileUploadResponse {
  success: boolean;
  message: string;
  file_id?: string;
  file_content?: string;
  old_outline?: string;
}

export interface AnalysisRequest {
  file_content: string;
  analysis_type: 'overview' | 'requirements' | 'outline_requirements' | 'disqualification' | 'quotation' | 'templates';
  selected_model: string; // 项目配置的AI模型名称
  project_id?: string; // 项目ID（用于查询结构化内容）
}

export interface OutlineRequest {
  overview: string;
  requirements: string;
  outline_requirements?: string; // 新增：目录要求
  uploaded_expand?: boolean;
  old_outline?: string;
  old_document?: string;
  selected_model: string; // 项目配置的AI模型名称
  // 新增：自定义提示词配置
  use_overview?: boolean;
  use_requirements?: boolean;
  use_outline_requirements?: boolean;
  overview_custom_prompt?: string;
  requirements_custom_prompt?: string;
  outline_requirements_custom_prompt?: string;
}

export interface ContentGenerationRequest {
  outline: { outline: any[] };
  project_overview: string;
}

export interface ChapterContentRequest {
  project_id: string;  // 项目ID
  chapter: any;
  parent_chapters?: any[];
  sibling_chapters?: any[];
  project_overview: string;
  selected_model: string;  // 项目配置中选择的模型
}

// 配置相关API
export const configApi = {
  // 保存配置
  saveConfig: (config: ConfigData) =>
    api.post('/api/config/save', config),

  // 加载配置
  loadConfig: () =>
    api.get('/api/config/load'),

  // 获取可用模型（后端代理，无需传配置）
  getModels: () =>
    api.get('/api/config/models'),
};

// 文档相关API
export const documentApi = {
  // 上传文件
  uploadFile: (file: File, fileCategory: 'tender' | 'other' = 'other', projectId?: string) => {
    const formData = new FormData();
    // 先添加非文件字段，再添加文件
    formData.append('file_category', fileCategory);
    if (projectId) {
      formData.append('project_id', projectId);
    }
    formData.append('file', file);
    return api.post<FileUploadResponse>('/api/document/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // 获取项目文件列表
  getProjectFiles: (projectId: string) => {
    return api.get(`/api/projects/${projectId}/files`);
  },

  // 删除项目文件
  deleteProjectFile: (projectId: string, fileId: string) => {
    return api.delete(`/api/projects/${projectId}/files/${fileId}`);
  },


  // 流式分析文档
  analyzeDocumentStream: async (data: AnalysisRequest) => {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/api/document/analyze-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    // 检查响应状态
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '请求失败' }));
      const errorMessage = errorData.detail || `请求失败: ${response.status}`;

      // 检查是否是 Token 欠费错误（手动触发，因为 fetch 不走 axios 拦截器）
      if (response.status === 400 && errorMessage.includes('Token') &&
          (errorMessage.includes('欠费') || errorMessage.includes('不足') || errorMessage.includes('额度'))) {
        console.log('💰 Token 欠费错误（fetch），触发欠费提示事件');
        window.dispatchEvent(new CustomEvent('token:overdraft', {
          detail: { message: errorMessage }
        }));
      }

      throw new Error(errorMessage);
    }

    return response;
  },

  // 解析项目名称
  parseProjectName: (fileContent: string) => {
    return api.post('/api/document/parse-project-name', { file_content: fileContent });
  },

  // 导出Word文档
  exportWord: (data: any) => {
    const token = localStorage.getItem('access_token');
    return fetch(`${API_BASE_URL}/api/document/export-word`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
  },

  // 使用AI提取最高限价
  extractMaxPrice: (content: string, selectedModel?: string) =>
    api.post('/api/document/extract-max-price', { content, selected_model: selectedModel }),
};

// 目录相关API
export const outlineApi = {
  // 生成目录
  generateOutline: (data: OutlineRequest) =>
    api.post('/api/outline/generate', data),

  // 流式生成目录
  generateOutlineStream: async (data: OutlineRequest, forceOverdraft: boolean = false) => {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/api/outline/generate-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(forceOverdraft ? { 'X-Force-Overdraft': 'true' } : {}),
      },
      body: JSON.stringify(data),
    });

    // 检查响应状态，手动触发 Token 欠费事件（fetch 不走 axios 拦截器）
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '请求失败' }));
      const errorMessage = errorData.detail || `请求失败: ${response.status}`;
      // 402 Payment Required 或 400 Bad Request 且包含 Token 相关错误
      if ((response.status === 402 || response.status === 400) && errorMessage.includes('Token') &&
          (errorMessage.includes('欠费') || errorMessage.includes('不足') || errorMessage.includes('额度'))) {
        // 检查 X-Token-Status 头来区分警告和超限
        const tokenStatus = response.headers.get('X-Token-Status');
        if (tokenStatus === 'warning' || response.status === 402) {
          // 欠费警告，让用户选择是否继续
          console.log('💰 Token 欠费警告（fetch），触发欠费警告事件');
          window.dispatchEvent(new CustomEvent('token:warning', { detail: { message: errorMessage } }));
        } else {
          // 超过最大欠费额度，必须充值
          console.log('💰 Token 超限错误（fetch），触发欠费提示事件');
          window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errorMessage } }));
        }
      }
      throw new Error(errorMessage);
    }

    return response;
  },

  // 智能分配字数
  allocateWords: (projectId: string, outline: any[], preserveLocked: boolean = true) =>
    api.post(`/api/outline/projects/${projectId}/allocate-words`, {
      outline,
      preserve_locked: preserveLocked,
    }),

  // 批量更新章节字数和锁定状态
  updateWordCount: (projectId: string, updates: Array<{
    item_id: string;
    expected_word_count?: number;
    is_locked?: boolean;
  }>) =>
    api.post(`/api/outline/projects/${projectId}/outline/update-word-count`, {
      updates,
    }),

  // 获取项目字数统计
  getWordStats: (projectId: string) =>
    api.get(`/api/outline/projects/${projectId}/word-stats`),

};

// 检查功能API
export const checkApi = {
  // 检查流程数据（全部子项）
  checkWorkflow: (projectId: string) =>
    api.get(`/api/check/workflow/${projectId}`),

  // 检查流程数据的单个子项
  checkWorkflowSubItem: (projectId: string, subItem: string) =>
    api.get(`/api/check/workflow/${projectId}/${subItem}`),

  // 获取检查结果（从数据库）
  getCheckResults: (projectId: string, module?: string) =>
    api.get(`/api/check/results/${projectId}`, {
      params: module ? { module } : undefined
    }),

  // 检查章节内容的单个子项
  checkContentSubItem: (projectId: string, subItem: string) =>
    api.get(`/api/check/content/${projectId}/${subItem}`),

  // 章节内容全量检查（每章一次LLM，同时检查所有AI子项）
  checkContentAll: (projectId: string) =>
    api.get(`/api/check/content/${projectId}/all`, { timeout: 300000 }),  // 5分钟超时

  // 获取章节内容全量检查实时进度
  getContentCheckProgress: (projectId: string) =>
    api.get(`/api/check/content/${projectId}/progress`),

  // 招标评分子项检查（单项）
  checkScoringSubItem: (projectId: string, subItem: string) =>
    api.get(`/api/check/scoring/${projectId}/${subItem}`),

  // 招标评分全量检查（并行执行5个子项）
  checkScoringAll: (projectId: string) =>
    api.get(`/api/check/scoring/${projectId}/all`, { timeout: 300000 }),  // 5分钟超时

  // 获取招标评分全量检查实时进度
  getScoringCheckProgress: (projectId: string) =>
    api.get(`/api/check/scoring/${projectId}/progress`),

  // 废标项风险扫描（AI实现）
  checkDisqualification: (projectId: string) =>
    api.get(`/api/check/disqualification/${projectId}`),

};

// 内容相关API
export const contentApi = {
  // 生成单章节内容
  generateChapterContent: (data: ChapterContentRequest) =>
    api.post('/api/content/generate-chapter', data),

  // 流式生成单章节内容
  generateChapterContentStream: async (data: ChapterContentRequest, signal?: AbortSignal, forceOverdraft: boolean = false) => {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/api/content/generate-chapter-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(forceOverdraft ? { 'X-Force-Overdraft': 'true' } : {}),
      },
      body: JSON.stringify(data),
      signal, // 添加 AbortSignal
    });

    // 检查响应状态，手动触发 Token 事件（fetch 不走 axios 拦截器）
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '请求失败' }));
      const errorMessage = errorData.detail || `请求失败: ${response.status}`;
      // 402: 欠费警告（弹出告警，用户可选择继续）
      if (response.status === 402) {
        console.log('Token 欠费警告，触发警告事件');
        window.dispatchEvent(new CustomEvent('token:warning', { detail: { message: errorMessage } }));
      }
      // 400: 超过最大欠费额度
      else if (response.status === 400 && errorMessage.includes('Token')) {
        console.log('Token 已超过最大欠费额度，触发欠费事件');
        window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errorMessage } }));
      }
      throw new Error(errorMessage);
    }

    return response;
  },
};

// 方案扩写相关API
export const expandApi = {
  // 上传方案扩写文件
  uploadExpandFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<FileUploadResponse>('/api/expand/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 文件上传专用超时设置：5分钟
    });
  },
};

// 公司管理API
export const companyApi = {
  // 创建公司
  createCompany: (data: any) =>
    api.post('/api/companies/', data),

  // 获取公司列表
  getCompanies: () =>
    api.get('/api/companies/'),

  // 获取公司详情
  getCompany: (id: string) =>
    api.get(`/api/companies/${id}`),

  // 更新公司
  updateCompany: (id: string, data: any) =>
    api.put(`/api/companies/${id}`, data),

  // 删除公司
  deleteCompany: (id: string) =>
    api.delete(`/api/companies/${id}`),

  // 识别营业执照
  recognizeBusinessLicense: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/companies/recognize-license', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// 项目管理API
export const projectApi = {
  // 创建项目
  createProject: (data: any) =>
    api.post('/api/projects/', data),

  // 获取项目列表
  getProjects: () =>
    api.get('/api/projects/'),

  // 获取项目详情
  getProject: (projectId: string, step?: number) => {
    const params = step !== undefined ? { step } : {};
    return api.get(`/api/projects/${projectId}`, { params });
  },

  // ========== 分步查询 API（按需加载、增量加载） ==========

  // 获取项目文件列表
  getProjectFiles: (projectId: string) =>
    api.get(`/api/projects/${projectId}/files`),

  // 获取项目解析结果
  getProjectAnalyses: (projectId: string) =>
    api.get(`/api/projects/${projectId}/analyses`),

  // 获取项目目录结构
  getProjectOutlines: (projectId: string) =>
    api.get(`/api/projects/${projectId}/outlines`),

  // 获取项目章节内容
  getProjectChapters: (projectId: string) =>
    api.get(`/api/projects/${projectId}/chapters`),

  // 更新项目
  updateProject: (projectId: string, data: any) =>
    api.put(`/api/projects/${projectId}`, data),

  // 删除项目
  deleteProject: (projectId: string) =>
    api.delete(`/api/projects/${projectId}`),

  // 复制项目
  copyProject: (projectId: string, data?: { name?: string; alias?: string; group_id?: string }) =>
    api.post(`/api/projects/${projectId}/copy`, data),

  // 保存解析结果
  saveAnalysis: (data: { project_id: string; analysis_type: string; content: string; version?: number }) =>
    api.post('/api/projects/analysis', data),

  // 获取解析结果
  getAnalysis: (projectId: string, analysisType: string) =>
    api.get(`/api/projects/${projectId}/analysis/${analysisType}`),

  // 获取所有解析结果
  getAllAnalysis: (projectId: string) =>
    api.get(`/api/projects/${projectId}/analysis`),

  // 保存目录
  saveOutline: (projectId: string, data: any) =>
    api.post(`/api/projects/${projectId}/outline`, data),

  // 获取目录
  getOutline: (projectId: string) =>
    api.get(`/api/projects/${projectId}/outline`),

  // 保存章节
  saveChapter: (data: { project_id: string; outline_item_id: string; content: string }) =>
    api.post('/api/projects/chapters', data),

  // 获取章节
  getChapter: (projectId: string, outlineItemId: string) =>
    api.get(`/api/projects/${projectId}/chapters/${outlineItemId}`),

  // 获取所有章节
  getChapters: (projectId: string) =>
    api.get(`/api/projects/${projectId}/chapters`),

  // 更新项目进度
  updateProgress: (projectId: string, data: any) =>
    api.post(`/api/projects/${projectId}/progress`, null, { params: data }),

  // 获取项目进度
  getProgress: (projectId: string) =>
    api.get(`/api/projects/${projectId}/progress`),

  // 保存全局AI生成方向
  saveAIDirection: (projectId: string, aiDirection: string) =>
    api.post('/api/projects/ai-direction', { project_id: projectId, ai_direction: aiDirection }),

  // 获取全局AI生成方向
  getAIDirection: (projectId: string) =>
    api.get(`/api/projects/${projectId}/ai-direction`),

  // 保存目录编号格式
  saveOutlineFormat: (projectId: string, outlineFormatId: string) =>
    api.post('/api/projects/outline-format', { project_id: projectId, outline_format_id: outlineFormatId }),

  // 获取目录编号格式
  getOutlineFormat: (projectId: string) =>
    api.get(`/api/projects/${projectId}/outline-format`),
};

// 认证相关API
export const authApi = {
  // 登录
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),

  // 注册
  register: (username: string, password: string, display_name: string) =>
    api.post('/api/auth/register', { username, password, display_name }),

  // 刷新 token
  refresh: (refresh_token: string) =>
    api.post('/api/auth/refresh', { refresh_token }),

  // 获取当前用户信息
  getCurrentUser: () =>
    api.get('/api/auth/me'),

  // 登出
  logout: () =>
    api.post('/api/auth/logout'),

  // 获取用户设置
  getSettings: () =>
    api.get('/api/auth/settings'),

  // 更新用户设置
  updateSettings: (data: { text_model?: string; vision_model?: string; display_name?: string }) =>
    api.put('/api/auth/settings', data),
};

// 分项报价相关API
export const quotationApi = {
  // 创建报价
  create: (data: any) =>
    api.post('/api/quotations', data),

  // 获取项目的所有报价版本
  getByProject: (projectId: string) =>
    api.get(`/api/quotations/project/${projectId}`),

  // 获取项目最新报价
  getLatest: (projectId: string) =>
    api.get(`/api/quotations/project/${projectId}/latest`),

  // 获取指定报价详情
  getById: (quotationId: number) =>
    api.get(`/api/quotations/${quotationId}`),

  // 更新报价
  update: (quotationId: number, data: any) =>
    api.put(`/api/quotations/${quotationId}`, data),

  // 删除报价
  delete: (quotationId: number) =>
    api.delete(`/api/quotations/${quotationId}`),

  // 克隆报价（创建新版本）
  clone: (quotationId: number) =>
    api.post(`/api/quotations/${quotationId}/clone`),
};

// ==================== 注记API ====================
export const annotationApi = {
  // 获取项目的所有注记
  getByProject: (projectId: string, outlineNodeId?: number) =>
    api.get(`/api/annotations/project/${projectId}`, {
      params: outlineNodeId ? { outline_node_id: outlineNodeId } : undefined
    }),

  // 获取指定目录节点的注记
  getByNode: (outlineNodeId: number) =>
    api.get(`/api/annotations/node/${outlineNodeId}`),

  // 创建注记
  create: (projectId: string, data: {
    outline_node_id: number;
    selected_text: string;
    content: string;
    position_data?: any;
  }) => api.post(`/api/annotations/project/${projectId}`, data),

  // 更新注记
  update: (annotationId: number, content: string) =>
    api.put(`/api/annotations/${annotationId}`, { content }),

  // 删除注记
  delete: (annotationId: number) =>
    api.delete(`/api/annotations/${annotationId}`),

  // 删除指定章节的所有注记
  deleteByNode: (outlineNodeId: number) =>
    api.delete(`/api/annotations/node/${outlineNodeId}`),
};

// ==================== Token管理API ====================
export const tokenApi = {
  // 获取当前用户Token余额
  getBalance: () =>
    api.get('/api/token/balance'),

  // 获取指定用户Token余额（管理员）
  getUserBalance: (userId: number) =>
    api.get(`/api/token/balance/${userId}`),

  // 充値Token（管理员）
  recharge: (data: {
    user_id: number;
    tokens_amount: number;
    payment_amount?: number;
    exchange_rate?: string;
    remark?: string;
    remark_type?: 'package' | 'custom'; // 备注类型，优先于remark自动生成标准备注
  }) => api.post('/api/token/recharge', data),

  // 获取用量日志
  getUsageLogs: (days: number = 30) =>
    api.get('/api/token/usage/logs', { params: { days } }),

  // 按操作类型统计
  getUsageByAction: (days: number = 30) =>
    api.get('/api/token/usage/by-action', { params: { days } }),

  // 按模型统计
  getUsageByModel: (days: number = 30) =>
    api.get('/api/token/usage/by-model', { params: { days } }),

  // 获取用量趋势数据（图表）
  getUsageTrend: (params: {
    period?: '24h' | '7d' | '30d' | '90d';
    granularity?: 'hour' | 'day' | 'week';
    start_date?: string;  // YYYY-MM-DD
    end_date?: string;    // YYYY-MM-DD
  } = {}) => api.get('/api/token/usage/trend', { params }),

  // 获取充值记录
  getRechargeRecords: (limit: number = 50) =>
    api.get('/api/token/recharge/records', { params: { limit } }),

  // 获取所有充值记录（管理员）
  getAllRechargeRecords: (days: number = 30, limit: number = 100) =>
    api.get('/api/token/recharge/all-records', { params: { days, limit } }),

  // 获取系统配置（管理员）
  getSystemConfigs: () =>
    api.get('/api/token/config'),

  // 更新系统配置（管理员）
  updateSystemConfig: (key: string, value: string, description?: string) =>
    api.put(`/api/token/config/${key}`, null, { params: { value, description } }),

  // 获取充值套餐列表
  getRechargePackages: () =>
    api.get('/api/auth/recharge/packages'),
};

// ==================== 人员管理API ====================
export const usersApi = {
  // 获取人员列表（需要管理员权限）
  getList: (keyword?: string) =>
    api.get('/api/users/list', { params: keyword ? { keyword } : undefined }),

  // 搜索用户（所有登录用户可用，用于项目组邀请）
  search: (keyword?: string) =>
    api.get('/api/users/search', { params: keyword ? { keyword } : undefined }),

  // 修改用户角色（仅 super）
  updateRole: (userId: number, role: string) =>
    api.put(`/api/users/${userId}/role`, { role }),
};

// ==================== 项目组管理API ====================
export const projectGroupApi = {
  // 获取当前用户的项目组列表
  getList: () =>
    api.get('/api/project-groups/list'),

  // 获取项目组详情
  getDetail: (groupId: string) =>
    api.get(`/api/project-groups/${groupId}`),

  // 创建项目组
  create: (data: { name: string; description?: string; owner_id?: number; member_ids?: number[] }) =>
    api.post('/api/project-groups/create', data),

  // 编辑项目组基本信息
  update: (groupId: string, data: { name?: string; description?: string; owner_id?: number }) =>
    api.put(`/api/project-groups/${groupId}`, data),

  // 更新项目组成员列表
  updateMembers: (groupId: string, member_ids: number[]) =>
    api.put(`/api/project-groups/${groupId}/members`, { member_ids }),

  // 退出项目组（成员自己退出）
  leave: (groupId: string) =>
    api.delete(`/api/project-groups/${groupId}/members/me`),

  // 移除项目组成员（负责人操作）
  removeMember: (groupId: string, userId: number) =>
    api.delete(`/api/project-groups/${groupId}/members/${userId}`),

  // 删除项目组
  delete: (groupId: string) =>
    api.delete(`/api/project-groups/${groupId}`),

  // 获取简化项目组列表（用于下拉选择）
  getSimpleList: () =>
    api.get('/api/project-groups/all/simple'),
};

// ==================== 邀请系统API ====================
export const invitationApi = {
  // 发送邀请
  send: (groupId: string, userId: number) =>
    api.post('/invitations/send', { group_id: groupId, user_id: userId }),

  // 验证邀请码
  verifyCode: (inviteCode: string) =>
    api.get(`/invitations/verify/${inviteCode}`),

  // 通过邀请码加入
  joinByCode: (inviteCode: string) =>
    api.post('/invitations/join-by-code', { invite_code: inviteCode }),

  // 处理邀请（接受/拒绝）
  process: (memberId: number, action: 'accept' | 'reject') =>
    api.post(`/invitations/${memberId}/process`, { action }),

  // 获取我收到的待处理邀请
  getMyInvitations: () =>
    api.get('/invitations/my-invitations'),

  // 获取我发送的邀请
  getSentInvitations: () =>
    api.get('/invitations/sent'),
};

// ==================== 消息通知API ====================
export const notificationApi = {
  // 获取通知列表
  getList: (params?: { unread_only?: boolean; limit?: number; offset?: number }) =>
    api.get('/notifications', { params }),

  // 获取未读数量
  getUnreadCount: () =>
    api.get('/notifications/unread-count'),

  // 标记通知为已读
  markAsRead: (notificationIds?: number[]) =>
    api.post('/notifications/mark-read', { notification_ids: notificationIds }),

  // 标记单个通知为已读
  markSingleAsRead: (notificationId: number) =>
    api.post(`/notifications/${notificationId}/mark-read`),

  // 删除通知
  delete: (notificationId: number) =>
    api.delete(`/notifications/${notificationId}`),

  // 删除所有已读通知
  deleteAllRead: () =>
    api.delete('/notifications'),
};

export default api;
